import moment from "moment";
import config from "config/config";
import { getName, getServiceRequestDate } from "js/formatting";
import { Tab } from "@headlessui/react";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { getTasks } from "service/policy/getTasks";
import { isFlagEnabled } from "js/utilities";
import { formatAgentFilteredPoliciesForDashboard } from "./utils";

import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";

import Carousel from "components/General/Carousel";
import SimpleDataRow from "components/General/SimpleDataRow";
import PolicySummaryCard from "components/PolicySummaryCard";
import AppNavigationCard from "components/General/AppNavigationCard";
import AgentCard from "components/AgentPortal/ModalDialogs/AgentCard";
import ReportCard from "components/AgentPortal/AgentDashboard/ReportCard";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PolicyLookupModal from "components/AgentPortal/ModalDialogs/PolicyLookupModal";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";
import PolicyLookUpFromComboBoxModal from "components/AgentPortal/ModalDialogs/PolicyLookUpFromComboBoxModal";

export default function DEPRICATED_Dashboard() {
  let dispatch = useDispatch();
  let navigate = useNavigate();
  const { t } = useTranslation();

  const { user } = useSelector((s) => s.user);
  const [isAgentLoading, setIsAgentLoading] = useState(false);

  const {
    agentPolicies,
    filteredPolicies,
    agentClaims,
    isAgentPoliciesLoading,
  } = useContext(AgentData);
  const policySummaries =
    formatAgentFilteredPoliciesForDashboard(filteredPolicies);
  const allowDownloadCommissionCheck = useAuthorizationCheck(24);

  const [isOpenPolicyLookup, setIsOpenPolicyLookup] = useState(false);
  const [isOpenPolicyLookupFromComboBox, setIsOpenPolicyLookupFromComboBox] =
    useState(false);
  const [isCommissionLoading, setIsCommissionLoading] = useState(false);
  const [commissionStatements, setCommissionStatements] = useState([]);
  const [services, setServices] = useState([]);

  // agent role check
  const masterAgent = user?.agentHierarchy?.find(
    (x) => x.isMasterAgent === true
  );
  const getPrimaryOwnerFullName = (contractID) => {
    let policy = agentPolicies?.find((p) => p.contractID === contractID);
    if (policy) {
      let party = policy?.parties?.find(
        (x) => x.person?.type?.toLowerCase() === "primary owner"
      );
      return getName(party.person);
    }
    return "";
  };

  useEffect(() => {
    // agent commission rows setup
    setIsCommissionLoading(true);
    dispatch(
      getCorrespondenceList({
        policyId: "",
        agentId: user?.corpAgentId ?? user?.agentViewingId,
        beginDate: moment()
          .subtract(
            parseInt(config.COMMISSION_CORRESPODENCE_DURATION),
            "months"
          )
          .format("MM/DD/YYYY"),
        endDate: moment().format("MM/DD/YYYY"),
        agentPolicies: agentPolicies,
      })
    )
      .then((response) => {
        setIsCommissionLoading(false);
        let comStatements = [];
        if (response.meta.requestStatus === "fulfilled" && response.payload) {
          response.payload.allAlerts.map((a) => {
            if (a.document?.typeOfDocument?.toUpperCase() === "COMMISSIONS") {
              comStatements.push(a);
            }
          });
          if (comStatements) {
            let copyCommissions = [...comStatements];
            copyCommissions?.sort(function (a, b) {
              if (
                new Date(a?.document?.dateOfDocument) >
                new Date(b?.document?.dateOfDocument)
              ) {
                return -1;
              }
              if (
                new Date(a?.document?.dateOfDocument) <
                new Date(b?.document?.dateOfDocument)
              ) {
                return 1;
              }
              return 0;
            });
            setCommissionStatements(copyCommissions);
          }
        }
      })
      .catch((error) => {
        setIsCommissionLoading(false);
      });
    if (agentPolicies?.length > 0) {
      let serviceList = [];
      setIsAgentLoading(true);
      getTasks({
        contractId_array: [
          ...agentPolicies.slice(0, 20).map((p) => p.contractID),
        ],
        agentId: "",
      })
        .then((response) => {
          setIsAgentLoading(false);
          const restrictedProcessServices =
            config.RESTRICTED_PROCESS_SERVICE_REQUEST.split(",");
          if (response?.length > 0) {
            //sort by date
            response?.sort(function (a, b) {
              const firstDate = getServiceRequestDate(a);
              const secondDate = getServiceRequestDate(b);
              if (new Date(firstDate) > new Date(secondDate)) {
                return -1;
              }
              if (new Date(firstDate) < new Date(secondDate)) {
                return 1;
              }
              return 0;
            });
            response?.forEach((service) => {
              if (
                !restrictedProcessServices.includes(
                  service.process.toLowerCase()
                ) &&
                service?.status?.toLowerCase() !== "e"
              ) {
                let oldPolicy = agentPolicies?.find(
                  (p) => p.contractID === service.policyID.substring(2)
                )?.oldPolicy;
                if (oldPolicy)
                  serviceList.push({
                    ...service,
                    oldPolicy: oldPolicy,
                    claimStatus: service?.status,
                    claimNumber: service.taskID,
                    primaryOwnerFullName: getPrimaryOwnerFullName(
                      service.policyID.substring(2)
                    ),
                  });
              }
            });
          }
          setServices(serviceList);
        })
        .catch((error) => {
          setIsAgentLoading(false);
        });
    }
  }, [agentPolicies]);

  useBreadcrumb([]);

  return (
    <EverlakeLoadingOverlay
      isLoading={isAgentLoading}
      loadingText={t("AgentDashboardDeprecated-loadingText")}
    >
      {filteredPolicies ? (
        <>
          <div className="container mb-8">
            <h1>
              <Trans
                defaults={t("AgentDashboard-welcomeMessage")}
                values={{ name: `${user.firstName} ${user.lastName}` }}
              />
            </h1>
            <hr />
          </div>

          <Carousel policies={agentPolicies} />

          {/* Things You Can Do */}
          <div className="container ui-component-spacing">
            <h2>{t("AgentDashboard-sectionOneTitle")}</h2>
            <p className="text-sm">{t("AgentDashboard-sectionOneSubtitle")}</p>
            <hr />

            <div className="grid grid-cols-1 auto-rows-fr gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              <AppNavigationCard
                icon={t("AppNavigationCard-accessMyCommission-icon")}
                url={t("AppNavigationCard-accessMyCommission-url")}
                title={t("AppNavigationCard-accessMyCommission-title")}
                desc={t("AppNavigationCard-accessMyCommission-subtitle")}
              />
              {config.CALCFOCUS_SERVICE_BASE_URL ? (
                <AppNavigationCard
                  icon={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-icon"
                  )}
                  title={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-title"
                  )}
                  desc={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-subtitle"
                  )}
                  type={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-type"
                  )}
                  openDialog={() => setIsOpenPolicyLookupFromComboBox(true)}
                />
              ) : (
                <AppNavigationCard
                  icon={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-icon"
                  )}
                  title={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-title"
                  )}
                  desc={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-subtitle"
                  )}
                  type={t(
                    "AppNavigationCard-runCustomizedQuotesAndIllustrations-type"
                  )}
                />
              )}

              <AppNavigationCard
                icon={t("AppNavigationCard-accessForms-icon")}
                url={t("AppNavigationCard-accessForms-url")}
                desc={t("AppNavigationCard-accessForms-subtitle")}
                title={t("AppNavigationCard-accessForms-title")}
              />

              <AppNavigationCard
                icon={t("AppNavigationCard-exploreHelpSection-icon")}
                url={t("AppNavigationCard-exploreHelpSection-url")}
                desc={t("AppNavigationCard-exploreHelpSection-subtitle")}
                title={t("AppNavigationCard-exploreHelpSection-title")}
              />

              <AppNavigationCard
                icon={t("AppNavigationCard-policyLookUp-icon")}
                type={t("AppNavigationCard-policyLookUp-type")}
                desc={t("AppNavigationCard-policyLookUp-subtitle")}
                title={t("AppNavigationCard-policyLookUp-title")}
                openDialog={() => setIsOpenPolicyLookup(true)}
              />

              <AppNavigationCard
                icon={t("AppNavigationCard-fileAClaim-icon")}
                desc={t("AppNavigationCard-fileAClaim-subtitle")}
                title={t("AppNavigationCard-fileAClaim-title")}
                url={t("AppNavigationCard-fileAClaim-url")}
              />
            </div>
          </div>
          <EverlakeLoadingOverlay
            isLoading={isAgentPoliciesLoading}
            loadingText={t("AgentDashboard-loadingText")}
            spinnerSize="sm"
          >
            <div className="container ui-component-spacing">
              <h2>{t("AgentDashboard-sectiontwotitle")}</h2>
              <p className="text-sm">
                {t("AgentDashboard-sectionTwoSubtitle")}
              </p>

              <hr />

              {masterAgent ? (
                <Tab.Group>
                  <div>
                    <div className="ui-card flex-grow px-2 p-1 md:px-4">
                      <Tab.List>
                        <Tab
                          className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg`}
                        >
                          {({ selected }) => (
                            <span
                              className={!selected ? "text-ui-blue" : undefined}
                            >
                              {t("AgentDashboardDeprecated-tab-1-title")}
                            </span>
                          )}
                        </Tab>
                        <Tab
                          className={`text-sm mx-3 pt-3 pb-2 mb-1 md:text-base md:mx-4 lg:text-lg`}
                        >
                          {({ selected }) => (
                            <span
                              className={!selected ? "text-ui-blue" : undefined}
                            >
                              {t("AgentDashboardDeprecated-tab-2-title")}
                            </span>
                          )}
                        </Tab>
                      </Tab.List>
                    </div>
                    <div className="mt-4 px-2">
                      <div className="text-xs grid gap-4 grid-cols-[max-content,max-content,max-content] content-center place-content-end">
                        <button
                          className="ui-btn-hyperlink ui-link-icon font-semibold"
                          onClick={() => setIsOpenPolicyLookup(true)}
                        >
                          <svg
                            className="ui-icon w-4 h-4"
                            focusable="false"
                            aria-hidden="true"
                            role="presentation"
                          >
                            <use href="#ui-icon-search"></use>
                          </svg>
                          {t("AgentDashboardDeprecated-policyLookupButton")}
                        </button>
                      </div>
                    </div>
                  </div>
                  <Tab.Panels>
                    <Tab.Panel>
                      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:md:grid-cols-4 lg:mt-6 lg:gap-6">
                        {/* policies summary card */}

                        {policySummaries.map((summary, index) => {
                          return (
                            summary?.number > 0 && (
                              <PolicySummaryCard
                                data={summary}
                                key={`summary - ${index}`}
                              />
                            )
                          );
                        })}
                      </div>
                    </Tab.Panel>
                    <Tab.Panel>
                      <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:md:grid-cols-4 lg:mt-6 lg:gap-6">
                        {/* Agent Cards */}
                        {user?.agentHierarchy.map((agent, index) => {
                          return (
                            <AgentCard
                              data={agent}
                              manager={getName(
                                masterAgent?.agentInfo?.parties?.person
                              )}
                              key={`agent - ${index}`}
                            />
                          );
                        })}
                      </div>
                    </Tab.Panel>
                  </Tab.Panels>
                </Tab.Group>
              ) : (
                <>
                  <div className="mt-4 px-2">
                    <div className="text-xs grid gap-4 grid-cols-[max-content,max-content,max-content] content-center place-content-end">
                      <button
                        className="ui-btn-hyperlink ui-link-icon font-semibold"
                        onClick={() => setIsOpenPolicyLookup(true)}
                      >
                        <svg
                          className="ui-icon w-4 h-4"
                          focusable="false"
                          aria-hidden="true"
                          role="presentation"
                        >
                          <use href="#ui-icon-search"></use>
                        </svg>
                        {t("AgentDashboardDeprecated-policyLookupButton")}
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:md:grid-cols-4 lg:mt-6 lg:gap-6">
                    {/* policies summary card */}

                    {policySummaries.map((summary, index) => {
                      return (
                        summary?.number > 0 && (
                          <PolicySummaryCard
                            data={summary}
                            key={`summary - ${index}`}
                            policies={filteredPolicies}
                          />
                        )
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </EverlakeLoadingOverlay>

          <div className="container ui-component-spacing">
            <hr />
          </div>

          {/* Monthly commission statement and Recent Reports */}
          <div className="container">
            {allowDownloadCommissionCheck &&
              (user?.role.id !== 4 ||
                (user?.role.id === 4 && user?.viewCommissions === 1)) && (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:gap-6">
                    <div className="ui-component-spacing">
                      <h2>{t("AgentDashboard-sectionThreeTitle")}</h2>
                      <hr />
                      <EverlakeLoadingOverlay
                        isLoading={isCommissionLoading}
                        loadingText={t(
                          "AgentDashboardDeprecated-sectionThree-commissionReport-loadingText"
                        )}
                        spinnerSize="sm"
                      >
                        <div className="ui-card grid grid-cols-1 place-content-between">
                          <ReportCard data={commissionStatements.slice(0, 5)} />
                          <button
                            className="ui-card-btn-more"
                            onClick={() => navigate(`/commissions`)}
                          >
                            {t(
                              "AgentDashboardDeprecated-sectionThree-commissionReport-button"
                            )}
                          </button>
                        </div>
                      </EverlakeLoadingOverlay>
                    </div>
                  </div>
                </>
              )}
          </div>
          {/* <!-- Requests & Claims --> */}
          <div className="container ui-component-spacing">
            <div className={`lg:grid lg:gap-x-4 lg:grid-cols-2 mb-8`}>
              <div>
                <div role="tablist" aria-label="My Claims Requests">
                  <p className="text-lg font-semibold max-w-sm p-0">
                    {t("AgentDashboard-sectionFour-tab-1-title")}
                  </p>
                </div>
                <div className="ui-card ui-component-spacing">
                  {agentClaims.length > 0 ? (
                    agentClaims
                      .slice(0, 4)
                      .map((claim, idx) => (
                        <SimpleDataRow
                          type="agentServicesAndClaims"
                          key={`Agent claim - ${idx}`}
                          data={claim}
                        />
                      ))
                  ) : (
                    <div className="relative mt-4"></div> //
                  )}
                  <Link
                    to="/claims"
                    className="text-center ui-card-btn-more"
                    aria-label="View more claims requests"
                  >
                    {t("AgentDashboard-sectionFour-agentClaim-button")}
                  </Link>
                </div>
              </div>
              {isFlagEnabled("ENABLE_SERVICE_TRACKING") && (
                <div>
                  <div role="tablist" aria-label="My Services Requests">
                    <p className="text-lg font-semibold max-w-sm p-0">
                      {t("AgentDashboard-service-title")}
                    </p>
                  </div>
                  <div className="ui-card ui-component-spacing">
                    {services.length > 0 ? (
                      services
                        .slice(0, 4)
                        .map((claim, idx) => (
                          <SimpleDataRow
                            type="agentServicesAndClaims"
                            key={`Agent services - ${idx}`}
                            data={claim}
                          />
                        ))
                    ) : (
                      <div className="relative mt-4"></div>
                    )}
                    <Link
                      to="/services"
                      className="text-center ui-card-btn-more"
                      aria-label="View more service requests"
                    >
                      {t("AgentDashboard-sectionFour-agentClaim-button")}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* <!-- Requests & Claims --> */}

          {/* Policy Lookup Dialog */}
          <PolicyLookupModal
            isOpenPolicyLookup={isOpenPolicyLookup}
            setIsOpenPolicyLookup={setIsOpenPolicyLookup}
          />
          <PolicyLookUpFromComboBoxModal
            isOpenPolicyLookup={isOpenPolicyLookupFromComboBox}
            setIsOpenPolicyLookup={setIsOpenPolicyLookupFromComboBox}
          />
        </>
      ) : (
        <div className="container mb-8">
          {!isAgentPoliciesLoading && (
            <p> {t("AgentDashboardDeprecated-no-policy-display")} </p>
          )}
        </div>
      )}
    </EverlakeLoadingOverlay>
  );
}
